import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Users,
  Crown,
  ShoppingCart,
  Wallet,
  TrendingUp,
  BellRing,
  Send,
  AlertTriangle,
} from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";
import { toast } from "sonner";
import api from "@/lib/api";
import { formatFCFA } from "@/lib/format";

const KpiCard = ({ icon: Icon, label, value, accent, testId }) => (
  <div
    data-testid={testId}
    className="bg-white rounded-lg border border-slate-200 p-6 hover:-translate-y-0.5 hover:shadow-md transition-[transform,box-shadow] duration-200"
  >
    <div className="flex items-center justify-between mb-4">
      <span className="text-xs font-bold uppercase tracking-[0.15em] text-slate-400">
        {label}
      </span>
      <div className={`h-9 w-9 rounded-lg flex items-center justify-center ${accent}`}>
        <Icon className="h-[18px] w-[18px]" />
      </div>
    </div>
    <p className="font-display text-3xl font-bold text-slate-900 tabular">{value}</p>
  </div>
);

export default function Dashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [reminders, setReminders] = useState([]);
  const [sending, setSending] = useState(null);

  const load = () => {
    api.get("/dashboard/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/donna/reminders").then((r) => setReminders(r.data)).catch(() => {});
  };

  useEffect(load, []);

  const sendReminder = async (client) => {
    setSending(client.id);
    try {
      const res = await api.post(`/donna/send/${client.id}`);
      if (res.data.sent) {
        toast.success(`Relance WhatsApp envoyée à ${client.name}`);
      } else if (res.data.configured === false) {
        toast.warning("WhatsApp non configuré — message préparé mais non envoyé", {
          description: res.data.message,
        });
      } else {
        toast.error(res.data.error || "Échec de l'envoi WhatsApp");
      }
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Échec de l'envoi");
    } finally {
      setSending(null);
    }
  };

  if (!stats) {
    return <div className="text-slate-400">Chargement…</div>;
  }

  return (
    <div className="max-w-7xl mx-auto space-y-8 animate-in-up" data-testid="dashboard-page">
      <div>
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400 mb-1">
          Vue d'ensemble
        </p>
        <h1 className="font-display text-4xl font-bold tracking-tight text-slate-900">
          Tableau de bord
        </h1>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <KpiCard
          testId="kpi-revenue"
          icon={Wallet}
          label="Revenus totaux"
          value={formatFCFA(stats.total_revenue)}
          accent="bg-sky-50 text-sky-600"
        />
        <KpiCard
          testId="kpi-club"
          icon={Crown}
          label="Adhésions Club"
          value={formatFCFA(stats.club_revenue)}
          accent="bg-amber-50 text-amber-600"
        />
        <KpiCard
          testId="kpi-members"
          icon={Users}
          label="Membres actifs"
          value={stats.active_members}
          accent="bg-emerald-50 text-emerald-600"
        />
        <KpiCard
          testId="kpi-orders"
          icon={ShoppingCart}
          label="Commandes"
          value={stats.total_orders}
          accent="bg-violet-50 text-violet-600"
        />
      </div>

      {stats.low_stock && stats.low_stock.length > 0 && (
        <div data-testid="low-stock-alert" className="rounded-lg border border-amber-200 bg-amber-50 p-5">
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle className="h-5 w-5 text-amber-600" />
            <h3 className="font-display text-lg font-semibold text-amber-800">
              Stock faible ({stats.low_stock.length})
            </h3>
          </div>
          <div className="flex flex-wrap gap-2">
            {stats.low_stock.map((p) => (
              <span key={p.id} data-testid={`low-stock-item-${p.id}`} className="text-sm font-medium px-3 py-1.5 rounded-lg bg-white border border-amber-200 text-amber-800">
                {p.name} · <span className="tabular font-bold">{p.stock}</span> restant(s)
              </span>
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Chart */}        <div className="lg:col-span-2 bg-white rounded-lg border border-slate-200 p-6">
          <div className="flex items-center gap-2 mb-6">
            <TrendingUp className="h-5 w-5 text-sky-500" />
            <h3 className="font-display text-xl font-semibold text-slate-800">
              Revenus mensuels
            </h3>
          </div>
          {stats.monthly_revenue.length === 0 ? (
            <div className="h-64 flex items-center justify-center text-slate-400 text-sm">
              Aucune donnée pour l'instant
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={stats.monthly_revenue}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                <XAxis dataKey="month" tick={{ fill: "#94a3b8", fontSize: 12 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fill: "#94a3b8", fontSize: 12 }} axisLine={false} tickLine={false} />
                <Tooltip
                  formatter={(v) => formatFCFA(v)}
                  contentStyle={{ borderRadius: 8, border: "1px solid #e2e8f0" }}
                />
                <Bar dataKey="amount" fill="#0ea5e9" radius={[6, 6, 0, 0]} maxBarSize={48} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Donna reminders */}
        <div className="bg-white rounded-lg border border-slate-200 p-6" data-testid="donna-widget">
          <div className="flex items-center gap-2 mb-1">
            <div className="h-8 w-8 rounded-lg bg-emerald-50 flex items-center justify-center">
              <BellRing className="h-4 w-4 text-emerald-600" />
            </div>
            <h3 className="font-display text-xl font-semibold text-slate-800">Donna</h3>
          </div>
          <p className="text-sm text-slate-500 mb-5">Relances de renouvellement (J-7)</p>

          {reminders.length === 0 ? (
            <div className="text-sm text-slate-400 py-8 text-center">
              Aucune adhésion à relancer 🎉
            </div>
          ) : (
            <div className="space-y-3">
              {reminders.map((c) => (
                <div
                  key={c.id}
                  className="rounded-lg border border-emerald-100 bg-emerald-50/50 p-3"
                  data-testid={`reminder-${c.id}`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <p className="font-medium text-slate-800 text-sm">{c.name}</p>
                    <span className="text-xs font-bold text-amber-700 bg-amber-100 px-2 py-0.5 rounded-full">
                      J-{c.pass_days_left}
                    </span>
                  </div>
                  <button
                    data-testid={`send-reminder-${c.id}`}
                    onClick={() => sendReminder(c)}
                    disabled={sending === c.id}
                    className="w-full flex items-center justify-center gap-1.5 py-1.5 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold transition-colors duration-200 disabled:opacity-60"
                  >
                    <Send className="h-3.5 w-3.5" />
                    {sending === c.id ? "Envoi…" : "Relancer via WhatsApp"}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
